import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getBackendUrl, getImageUrl } from '../utils/api';

const API = getBackendUrl();

// Logo URL - Fallen Dragon Logo
const LOGO_URL = "https://customer-assets.emergentagent.com/job_fallen-events-hub/artifacts/9a9pm8c7_Fallen%20Dragon%20Logo.jpeg";

// Dark forest background
const FOREST_BG = "https://images.unsplash.com/photo-1608096344583-844d5767bea4?auto=format&fit=crop&w=1920&q=80";

const EventDetails = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const disclaimerRef = useRef(null);
  
  const [event, setEvent] = useState(null);
  const [disclaimer, setDisclaimer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [acceptedDisclaimer, setAcceptedDisclaimer] = useState(false);
  const [disclaimerScrolled, setDisclaimerScrolled] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    address: '',
    postcode: '',
    emergency_contact_name: '',
    emergency_contact_phone: ''
  });
  
  // Signature state
  const [signatureType, setSignatureType] = useState('type'); // 'type' or 'draw'
  const [typedSignature, setTypedSignature] = useState('');
  const [drawnSignature, setDrawnSignature] = useState(null);
  const signatureCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  
  // Parent/Guardian state (for under 18)
  const [parentGuardian, setParentGuardian] = useState({
    name: '',
    relationship: 'Parent',
    phone: '',
    signatureType: 'type',
    typedSignature: '',
    drawnSignature: null
  });
  const parentSignatureCanvasRef = useRef(null);
  const [isParentDrawing, setIsParentDrawing] = useState(false);
  
  // Calculate if participant is under 18
  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };
  
  const participantAge = calculateAge(formData.date_of_birth);
  const isMinor = participantAge !== null && participantAge < 18;
  
  // Check if user has scrolled to bottom of disclaimer
  const checkScrollPosition = useCallback(() => {
    if (disclaimerRef.current) {
      const el = disclaimerRef.current;
      const { scrollTop, scrollHeight, clientHeight } = el;
      
      // Check if scrolled to bottom (with threshold) OR if content doesn't need scrolling
      const isAtBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight - 5;
      const noScrollNeeded = scrollHeight <= clientHeight + 5;
      
      if (isAtBottom || noScrollNeeded) {
        setDisclaimerScrolled(true);
      }
    }
  }, []);
  
  // Check scroll position when disclaimer loads or booking form opens
  useEffect(() => {
    if (disclaimer && showBookingForm) {
      // Reset scroll state when form opens
      setDisclaimerScrolled(false);
      setAcceptedDisclaimer(false);
      
      // Delay to ensure content is rendered, then check
      const timer = setTimeout(() => {
        checkScrollPosition();
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [disclaimer, showBookingForm, checkScrollPosition]);
  
  // Waitlist state
  const [showWaitlistForm, setShowWaitlistForm] = useState(false);
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(null);
  const [waitlistForm, setWaitlistForm] = useState({
    name: '',
    email: '',
    phone: ''
  });

  // Signature canvas drawing functions
  const startDrawing = (e, canvasRef, setDrawing) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
  };

  const draw = (e, canvasRef, isCurrentlyDrawing, setSignatureData) => {
    if (!isCurrentlyDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#ffffff';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = (canvasRef, setDrawing, setSignatureData) => {
    setDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) {
      setSignatureData(canvas.toDataURL('image/png'));
    }
  };

  const clearCanvas = (canvasRef, setSignatureData) => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setSignatureData(null);
    }
  };

  useEffect(() => {
    fetchEventDetails();
    fetchDisclaimer();
  }, [eventId]);

  const fetchEventDetails = async () => {
    try {
      const response = await fetch(`${API}/booking/events/${eventId}`);
      const data = await response.json();
      if (data.success) {
        setEvent(data.event);
      } else {
        setError('Event not found');
      }
    } catch (error) {
      console.error('Error fetching event:', error);
      setError('Failed to load event details');
    } finally {
      setLoading(false);
    }
  };

  const fetchDisclaimer = async () => {
    try {
      const response = await fetch(`${API}/booking/disclaimer`);
      const data = await response.json();
      if (data.success && data.disclaimer) {
        setDisclaimer(data.disclaimer);
      }
    } catch (error) {
      console.error('Error fetching disclaimer:', error);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'long',
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const handleTeamSelect = (team) => {
    if (team.spots_available > 0) {
      setSelectedTeam(team);
      setShowBookingForm(true);
      setError('');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!selectedTeam) {
      setError('Please select a team');
      return;
    }

    if (disclaimer && !acceptedDisclaimer) {
      setError('You must accept the disclaimer to proceed');
      return;
    }

    // Validate required fields with specific messages
    const missingFields = [];
    if (!formData.name) missingFields.push('Full Name');
    if (!formData.email) missingFields.push('Email');
    if (!formData.phone) missingFields.push('Contact Number');
    if (!formData.date_of_birth) missingFields.push('Date of Birth');
    if (!formData.address) missingFields.push('Address');
    if (!formData.postcode) missingFields.push('Postcode');
    if (!formData.emergency_contact_name) missingFields.push('Emergency Contact Name');
    if (!formData.emergency_contact_phone) missingFields.push('Emergency Contact Phone');
    
    if (missingFields.length > 0) {
      setError(`Please fill in: ${missingFields.join(', ')}`);
      return;
    }

    // Validate signature
    const hasParticipantSignature = signatureType === 'type' 
      ? typedSignature.trim() 
      : drawnSignature;
    
    if (!isMinor && !hasParticipantSignature) {
      setError('Please provide your signature');
      return;
    }

    // Validate parent/guardian for minors
    if (isMinor) {
      if (!parentGuardian.name || !parentGuardian.phone) {
        setError('Parent/Guardian details are required for participants under 18');
        return;
      }
      const hasParentSignature = parentGuardian.signatureType === 'type'
        ? parentGuardian.typedSignature.trim()
        : parentGuardian.drawnSignature;
      if (!hasParentSignature) {
        setError('Parent/Guardian signature is required');
        return;
      }
    }

    setSubmitting(true);

    try {
      // Build signature data
      const signatureData = {
        signature_type: signatureType,
        signature_data: signatureType === 'type' ? typedSignature : drawnSignature,
        signature_date: new Date().toISOString().split('T')[0],
        is_minor: isMinor
      };

      // Add parent/guardian data if minor
      if (isMinor) {
        signatureData.parent_guardian_name = parentGuardian.name;
        signatureData.parent_guardian_relationship = parentGuardian.relationship;
        signatureData.parent_guardian_phone = parentGuardian.phone;
        signatureData.parent_guardian_signature_type = parentGuardian.signatureType;
        signatureData.parent_guardian_signature_data = parentGuardian.signatureType === 'type' 
          ? parentGuardian.typedSignature 
          : parentGuardian.drawnSignature;
      }

      const response = await fetch(`${API}/booking/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Origin': window.location.origin
        },
        body: JSON.stringify({
          event_id: eventId,
          team_id: selectedTeam.id,
          customer: {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            date_of_birth: formData.date_of_birth,
            address: formData.address,
            postcode: formData.postcode,
            emergency_contact_name: formData.emergency_contact_name,
            emergency_contact_phone: formData.emergency_contact_phone
          },
          signature: signatureData,
          disclaimer_accepted: acceptedDisclaimer
        })
      });

      const data = await response.json();

      if (data.success && data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      } else {
        // Handle different error formats from backend
        let errorMessage = 'Failed to create booking';
        if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        } else if (Array.isArray(data.detail)) {
          // Pydantic validation errors come as array of objects
          errorMessage = data.detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
        } else if (data.detail?.msg) {
          errorMessage = data.detail.msg;
        } else if (data.message) {
          errorMessage = data.message;
        }
        setError(errorMessage);
        setSubmitting(false);
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      setError('Failed to create booking. Please try again.');
      setSubmitting(false);
    }
  };

  const handleJoinWaitlist = async (e) => {
    e.preventDefault();
    if (!waitlistForm.name || !waitlistForm.email) {
      setError('Please enter your name and email');
      return;
    }
    
    setWaitlistSubmitting(true);
    setError('');
    
    try {
      const response = await fetch(
        `${API}/booking/waitlist?event_id=${eventId}&name=${encodeURIComponent(waitlistForm.name)}&email=${encodeURIComponent(waitlistForm.email)}&phone=${encodeURIComponent(waitlistForm.phone || '')}`,
        { method: 'POST' }
      );
      
      const data = await response.json();
      
      if (data.success) {
        setWaitlistSuccess(data);
        setShowWaitlistForm(false);
      } else {
        setError(data.detail || 'Failed to join waitlist');
      }
    } catch (error) {
      console.error('Error joining waitlist:', error);
      setError('Failed to join waitlist. Please try again.');
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  // Check if event is sold out
  const isSoldOut = event?.teams?.every(team => team.spots_available <= 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-gray-800 border-t-red-800 rounded-full animate-spin mb-4"></div>
          <p className="text-gray-500">Loading mission briefing...</p>
        </div>
      </div>
    );
  }

  if (error && !event) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <Link to="/" className="text-gray-400 hover:text-white underline">
            ← Return to base
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black relative">
      {/* Background */}
      <div 
        className="fixed inset-0 bg-cover bg-center opacity-20"
        style={{ backgroundImage: `url(${FOREST_BG})` }}
      />
      <div className="fixed inset-0 bg-gradient-to-b from-black via-black/90 to-black" />
      
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-sm border-b border-gray-900">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src={LOGO_URL} alt="Fallen Dragon Events" className="h-12 w-auto" />
          </Link>
          <Link to="/" className="text-gray-400 hover:text-white transition-colors">
            ← Back to Events
          </Link>
        </div>
      </nav>

      {/* Main Content */}
      <main className="relative z-10 pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          
          {/* Event Header */}
          <div className="mb-12">
            {event.image_url && (
              <div className="w-full rounded-lg overflow-hidden mb-8 border border-gray-800 bg-black/30 flex items-center justify-center">
                <img 
                  src={getImageUrl(event.image_url)} 
                  alt={event.name}
                  className="max-w-full max-h-96 object-contain opacity-80"
                />
              </div>
            )}
            
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 tracking-wide"
                style={{ textShadow: '0 2px 20px rgba(0,0,0,0.8)' }}>
              {event.name}
            </h1>
            
            <div className="flex flex-wrap gap-6 text-gray-400">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2 text-red-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {event.end_date && event.end_date !== event.date
                  ? `${formatDate(event.date)} - ${formatDate(event.end_date)}`
                  : formatDate(event.date)
                }
              </div>
              {event.time && (
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2 text-red-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {event.time}{event.end_time ? ` - ${event.end_time}` : ''}
                </div>
              )}
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2 text-red-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                </svg>
                {event.location}
              </div>
            </div>
            
            {event.description && (
              <p className="mt-6 text-gray-400 leading-relaxed">
                {event.description}
              </p>
            )}
          </div>

          {/* Price Display */}
          <div className="bg-black/60 backdrop-blur-sm rounded-lg p-6 mb-8 border border-gray-800">
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Deployment Cost</span>
              <span className="text-3xl font-bold text-red-500">£{event.price?.toFixed(2)}</span>
            </div>
          </div>

          {/* Team Selection */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-white mb-4 tracking-wide">
              SELECT YOUR TEAM
            </h2>
            
            <div className="grid gap-4 md:grid-cols-2">
              {event.teams?.map((team, index) => {
                const spotsAvailable = team.capacity - (team.current_count || 0);
                const teamId = team.id || `team-${index}`;
                const isSelected = selectedTeam?.id === team.id;
                const isFull = spotsAvailable <= 0;
                
                return (
                  <button
                    key={teamId}
                    onClick={() => handleTeamSelect({ ...team, spots_available: spotsAvailable })}
                    disabled={isFull}
                    className={`p-6 rounded-lg border-2 text-left transition-all duration-300 relative ${
                      isSelected 
                        ? 'border-green-500 bg-green-950/40 ring-2 ring-green-500/50' 
                        : isFull 
                          ? 'border-gray-800 bg-black/40 opacity-50 cursor-not-allowed'
                          : 'border-gray-700 bg-black/60 hover:border-gray-500 hover:bg-black/70'
                    }`}
                  >
                    {/* Selected indicator badge */}
                    {isSelected && (
                      <div className="absolute -top-2 -right-2 bg-green-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">
                        SELECTED
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-2">
                      <h3 className={`text-lg font-bold ${isSelected ? 'text-green-400' : 'text-white'}`}>{team.name}</h3>
                      {isSelected && (
                        <svg className="w-6 h-6 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    
                    {team.description && (
                      <p className="text-sm text-gray-500 mb-3">{team.description}</p>
                    )}
                    
                    <div className="flex items-center justify-between">
                      <span className={`text-sm ${isFull ? 'text-red-600' : 'text-gray-500'}`}>
                        {isFull ? 'FULL' : `${spotsAvailable} of ${team.capacity} spots`}
                      </span>
                      
                      {/* Capacity bar */}
                      <div className="w-24 h-2 bg-gray-900 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all ${isFull ? 'bg-red-700' : 'bg-gray-600'}`}
                          style={{ width: `${((team.current_count || 0) / team.capacity) * 100}%` }}
                        />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            
            {/* Waitlist Section - Shows when all teams are full */}
            {isSoldOut && !waitlistSuccess && (
              <div className="mt-6 bg-red-950/30 border border-red-800/50 rounded-lg p-6">
                <div className="text-center">
                  <svg className="w-12 h-12 mx-auto mb-3 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-lg font-bold text-white mb-2">This Event is Sold Out</h3>
                  <p className="text-gray-400 mb-4">
                    All teams are at full capacity. Join the waitlist to be notified if a spot becomes available.
                  </p>
                  
                  {!showWaitlistForm ? (
                    <button
                      onClick={() => setShowWaitlistForm(true)}
                      className="px-6 py-3 bg-red-800 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors"
                      data-testid="join-waitlist-btn"
                    >
                      Join Waitlist
                    </button>
                  ) : (
                    <form onSubmit={handleJoinWaitlist} className="text-left space-y-4 mt-4">
                      <div>
                        <label className="block text-gray-400 text-sm mb-1">Name *</label>
                        <input
                          type="text"
                          value={waitlistForm.name}
                          onChange={(e) => setWaitlistForm(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-4 py-2 bg-black border border-gray-800 rounded text-white focus:border-red-800 focus:outline-none"
                          placeholder="Your name"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-gray-400 text-sm mb-1">Email *</label>
                        <input
                          type="email"
                          value={waitlistForm.email}
                          onChange={(e) => setWaitlistForm(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full px-4 py-2 bg-black border border-gray-800 rounded text-white focus:border-red-800 focus:outline-none"
                          placeholder="your@email.com"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-gray-400 text-sm mb-1">Phone (optional)</label>
                        <input
                          type="tel"
                          value={waitlistForm.phone}
                          onChange={(e) => setWaitlistForm(prev => ({ ...prev, phone: e.target.value }))}
                          className="w-full px-4 py-2 bg-black border border-gray-800 rounded text-white focus:border-red-800 focus:outline-none"
                          placeholder="07xxx xxxxxx"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="submit"
                          disabled={waitlistSubmitting}
                          className="flex-1 py-3 bg-red-800 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                        >
                          {waitlistSubmitting ? 'Joining...' : 'Join Waitlist'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowWaitlistForm(false)}
                          className="px-4 py-3 border border-gray-700 text-gray-400 rounded-lg hover:bg-gray-900"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            )}
            
            {/* Waitlist Success */}
            {waitlistSuccess && (
              <div className="mt-6 bg-green-950/30 border border-green-800/50 rounded-lg p-6 text-center">
                <svg className="w-12 h-12 mx-auto mb-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-lg font-bold text-white mb-2">You're on the Waitlist!</h3>
                <p className="text-gray-400">
                  {waitlistSuccess.message}
                  {waitlistSuccess.position && ` You're #${waitlistSuccess.position} in line.`}
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  We'll email you if a spot opens up.
                </p>
              </div>
            )}
          </div>

          {/* Booking Form */}
          {showBookingForm && selectedTeam && (
            <div className="bg-black/60 backdrop-blur-sm rounded-lg p-8 border border-gray-800">
              <h2 className="text-xl font-bold text-white mb-6 tracking-wide">
                COMPLETE YOUR DEPLOYMENT
              </h2>
              
              <form onSubmit={handleSubmit}>
                <div className="space-y-6">
                  {/* Selected Team Summary */}
                  <div className="bg-black/60 p-4 rounded-lg border border-gray-800">
                    <p className="text-gray-500 text-sm">Selected Team</p>
                    <p className="text-white font-semibold">{selectedTeam.name}</p>
                  </div>

                  {/* Customer Details */}
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Call Sign (Full Name) *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-black border border-gray-800 rounded-lg text-white placeholder-gray-600 focus:border-red-800 focus:outline-none transition-colors"
                      placeholder="Enter your full name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Comms (Email) *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-black border border-gray-800 rounded-lg text-white placeholder-gray-600 focus:border-red-800 focus:outline-none transition-colors"
                      placeholder="your@email.com"
                    />
                    <p className="text-gray-600 text-xs mt-1">Your mission briefing will be sent here</p>
                  </div>
                  
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Contact Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-black border border-gray-800 rounded-lg text-white placeholder-gray-600 focus:border-red-800 focus:outline-none transition-colors"
                      placeholder="07XXX XXXXXX"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Date of Birth *</label>
                    <input
                      type="date"
                      name="date_of_birth"
                      value={formData.date_of_birth}
                      onChange={handleInputChange}
                      required
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 bg-black border border-gray-800 rounded-lg text-white placeholder-gray-600 focus:border-red-800 focus:outline-none transition-colors"
                    />
                    {participantAge !== null && (
                      <p className={`text-xs mt-1 ${isMinor ? 'text-amber-500' : 'text-gray-600'}`}>
                        Age: {participantAge} years {isMinor && '(Parent/Guardian signature required)'}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Address *</label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-black border border-gray-800 rounded-lg text-white placeholder-gray-600 focus:border-red-800 focus:outline-none transition-colors"
                      placeholder="Enter your full address"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Postcode *</label>
                    <input
                      type="text"
                      name="postcode"
                      value={formData.postcode}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-black border border-gray-800 rounded-lg text-white placeholder-gray-600 focus:border-red-800 focus:outline-none transition-colors uppercase"
                      placeholder="XX00 0XX"
                    />
                  </div>

                  {/* Emergency Contact Section */}
                  <div className="bg-black/40 p-4 rounded-lg border border-gray-800">
                    <p className="text-red-500 text-sm font-semibold mb-4 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      Emergency Contact Details
                    </p>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-gray-400 text-sm mb-2">Emergency Contact Name *</label>
                        <input
                          type="text"
                          name="emergency_contact_name"
                          value={formData.emergency_contact_name}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 bg-black border border-gray-800 rounded-lg text-white placeholder-gray-600 focus:border-red-800 focus:outline-none transition-colors"
                          placeholder="Full name of emergency contact"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-gray-400 text-sm mb-2">Emergency Contact Phone *</label>
                        <input
                          type="tel"
                          name="emergency_contact_phone"
                          value={formData.emergency_contact_phone}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 bg-black border border-gray-800 rounded-lg text-white placeholder-gray-600 focus:border-red-800 focus:outline-none transition-colors"
                          placeholder="07XXX XXXXXX"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Disclaimer */}
                  {disclaimer && (
                    <div className="bg-black/60 p-6 rounded-lg border border-gray-800">
                      <h3 className="text-white font-semibold mb-3">{disclaimer.title}</h3>
                      <div 
                        ref={disclaimerRef}
                        className="text-gray-500 text-sm mb-4 max-h-48 overflow-y-auto prose prose-sm prose-invert border border-gray-700 rounded p-3 bg-black/40"
                        dangerouslySetInnerHTML={{ __html: disclaimer.content }}
                        onScroll={checkScrollPosition}
                      />
                      
                      {!disclaimerScrolled && (
                        <p className="text-amber-500 text-xs mb-3 flex items-center gap-2">
                          <svg className="w-4 h-4 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                          </svg>
                          Please scroll through the entire disclaimer to enable acceptance
                        </p>
                      )}
                      
                      <label className={`flex items-start ${disclaimerScrolled ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                        <input
                          type="checkbox"
                          checked={acceptedDisclaimer}
                          onChange={(e) => {
                            if (disclaimerScrolled) {
                              setAcceptedDisclaimer(e.target.checked);
                            }
                          }}
                          disabled={!disclaimerScrolled}
                          className="mt-1 mr-3 w-5 h-5 rounded border-gray-700 bg-black text-red-700 focus:ring-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        <span className={`text-sm ${disclaimerScrolled ? 'text-gray-400' : 'text-gray-600'}`}>
                          I have read, understood, and agree to the above disclaimer/waiver. 
                          I confirm that all information provided is accurate.
                        </span>
                      </label>
                    </div>
                  )}

                  {/* Signature Section */}
                  {acceptedDisclaimer && (
                    <div className="bg-black/60 p-6 rounded-lg border border-gray-800 space-y-4">
                      <h3 className="text-white font-semibold">
                        {isMinor ? 'Parent/Guardian Signature' : 'Participant Signature'}
                      </h3>
                      <p className="text-gray-500 text-sm">
                        {isMinor 
                          ? 'As the participant is under 18, a parent or legal guardian must sign below.'
                          : 'Please sign below to confirm your agreement to the disclaimer.'}
                      </p>

                      {/* Parent/Guardian Details (if minor) */}
                      {isMinor && (
                        <div className="space-y-4 border-b border-gray-800 pb-4">
                          <div>
                            <label className="block text-gray-400 text-sm mb-2">Parent/Guardian Full Name *</label>
                            <input
                              type="text"
                              value={parentGuardian.name}
                              onChange={(e) => setParentGuardian(prev => ({ ...prev, name: e.target.value }))}
                              required
                              className="w-full px-4 py-3 bg-black border border-gray-800 rounded-lg text-white placeholder-gray-600 focus:border-red-800 focus:outline-none transition-colors"
                              placeholder="Enter parent/guardian full name"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-gray-400 text-sm mb-2">Relationship to Participant *</label>
                            <select
                              value={parentGuardian.relationship}
                              onChange={(e) => setParentGuardian(prev => ({ ...prev, relationship: e.target.value }))}
                              className="w-full px-4 py-3 bg-black border border-gray-800 rounded-lg text-white focus:border-red-800 focus:outline-none transition-colors"
                            >
                              <option value="Parent">Parent</option>
                              <option value="Legal Guardian">Legal Guardian</option>
                            </select>
                          </div>
                          
                          <div>
                            <label className="block text-gray-400 text-sm mb-2">Parent/Guardian Contact Phone *</label>
                            <input
                              type="tel"
                              value={parentGuardian.phone}
                              onChange={(e) => setParentGuardian(prev => ({ ...prev, phone: e.target.value }))}
                              required
                              className="w-full px-4 py-3 bg-black border border-gray-800 rounded-lg text-white placeholder-gray-600 focus:border-red-800 focus:outline-none transition-colors"
                              placeholder="07XXX XXXXXX"
                            />
                          </div>
                        </div>
                      )}

                      {/* Signature Type Toggle */}
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => isMinor 
                            ? setParentGuardian(prev => ({ ...prev, signatureType: 'type' }))
                            : setSignatureType('type')
                          }
                          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                            (isMinor ? parentGuardian.signatureType : signatureType) === 'type'
                              ? 'bg-red-900 text-white border border-red-700'
                              : 'bg-black text-gray-400 border border-gray-800 hover:border-gray-700'
                          }`}
                        >
                          Type Signature
                        </button>
                        <button
                          type="button"
                          onClick={() => isMinor
                            ? setParentGuardian(prev => ({ ...prev, signatureType: 'draw' }))
                            : setSignatureType('draw')
                          }
                          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                            (isMinor ? parentGuardian.signatureType : signatureType) === 'draw'
                              ? 'bg-red-900 text-white border border-red-700'
                              : 'bg-black text-gray-400 border border-gray-800 hover:border-gray-700'
                          }`}
                        >
                          Draw Signature
                        </button>
                      </div>

                      {/* Typed Signature */}
                      {(isMinor ? parentGuardian.signatureType : signatureType) === 'type' && (
                        <div>
                          <label className="block text-gray-400 text-sm mb-2">
                            {isMinor ? 'Parent/Guardian Signature (Type your full name) *' : 'Signature (Type your full name) *'}
                          </label>
                          <input
                            type="text"
                            value={isMinor ? parentGuardian.typedSignature : typedSignature}
                            onChange={(e) => isMinor
                              ? setParentGuardian(prev => ({ ...prev, typedSignature: e.target.value }))
                              : setTypedSignature(e.target.value)
                            }
                            className="w-full px-4 py-3 bg-black border border-gray-800 rounded-lg text-white placeholder-gray-600 focus:border-red-800 focus:outline-none transition-colors italic text-xl"
                            placeholder="Type your full legal name"
                            style={{ fontFamily: 'cursive, serif' }}
                          />
                        </div>
                      )}

                      {/* Drawn Signature */}
                      {(isMinor ? parentGuardian.signatureType : signatureType) === 'draw' && (
                        <div>
                          <label className="block text-gray-400 text-sm mb-2">
                            {isMinor ? 'Parent/Guardian Signature (Draw below) *' : 'Signature (Draw below) *'}
                          </label>
                          <div className="relative">
                            <canvas
                              ref={isMinor ? parentSignatureCanvasRef : signatureCanvasRef}
                              width={400}
                              height={150}
                              className="w-full bg-black border border-gray-700 rounded-lg cursor-crosshair touch-none"
                              onMouseDown={(e) => startDrawing(e, isMinor ? parentSignatureCanvasRef : signatureCanvasRef, isMinor ? setIsParentDrawing : setIsDrawing)}
                              onMouseMove={(e) => draw(e, isMinor ? parentSignatureCanvasRef : signatureCanvasRef, isMinor ? isParentDrawing : isDrawing, isMinor ? (data) => setParentGuardian(prev => ({ ...prev, drawnSignature: data })) : setDrawnSignature)}
                              onMouseUp={() => stopDrawing(isMinor ? parentSignatureCanvasRef : signatureCanvasRef, isMinor ? setIsParentDrawing : setIsDrawing, isMinor ? (data) => setParentGuardian(prev => ({ ...prev, drawnSignature: data })) : setDrawnSignature)}
                              onMouseLeave={() => stopDrawing(isMinor ? parentSignatureCanvasRef : signatureCanvasRef, isMinor ? setIsParentDrawing : setIsDrawing, isMinor ? (data) => setParentGuardian(prev => ({ ...prev, drawnSignature: data })) : setDrawnSignature)}
                              onTouchStart={(e) => startDrawing(e, isMinor ? parentSignatureCanvasRef : signatureCanvasRef, isMinor ? setIsParentDrawing : setIsDrawing)}
                              onTouchMove={(e) => draw(e, isMinor ? parentSignatureCanvasRef : signatureCanvasRef, isMinor ? isParentDrawing : isDrawing, isMinor ? (data) => setParentGuardian(prev => ({ ...prev, drawnSignature: data })) : setDrawnSignature)}
                              onTouchEnd={() => stopDrawing(isMinor ? parentSignatureCanvasRef : signatureCanvasRef, isMinor ? setIsParentDrawing : setIsDrawing, isMinor ? (data) => setParentGuardian(prev => ({ ...prev, drawnSignature: data })) : setDrawnSignature)}
                            />
                            <button
                              type="button"
                              onClick={() => clearCanvas(isMinor ? parentSignatureCanvasRef : signatureCanvasRef, isMinor ? (data) => setParentGuardian(prev => ({ ...prev, drawnSignature: data })) : setDrawnSignature)}
                              className="absolute top-2 right-2 px-3 py-1 text-xs bg-gray-800 text-gray-400 rounded hover:bg-gray-700 transition-colors"
                            >
                              Clear
                            </button>
                          </div>
                          <p className="text-gray-600 text-xs mt-1">Use your mouse or finger to sign above</p>
                        </div>
                      )}

                      {/* Signature Date */}
                      <div className="flex items-center justify-between pt-2 border-t border-gray-800">
                        <span className="text-gray-500 text-sm">Date of Signature:</span>
                        <span className="text-white text-sm">{new Date().toLocaleDateString('en-GB')}</span>
                      </div>
                    </div>
                  )}

                  {/* Error Message */}
                  {error && (
                    <div className="bg-red-950/50 border border-red-900 text-red-400 px-4 py-3 rounded-lg">
                      {error}
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-4 bg-red-900 hover:bg-red-800 disabled:bg-gray-900 disabled:cursor-not-allowed text-white font-semibold tracking-[0.15em] border border-red-700 transition-all duration-300 flex items-center justify-center"
                  >
                    {submitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                        PROCESSING...
                      </>
                    ) : (
                      <>
                        PROCEED TO PAYMENT - £{event.price?.toFixed(2)}
                      </>
                    )}
                  </button>
                  
                  <p className="text-center text-gray-600 text-xs">
                    Your slot will be held for 60 minutes while you complete payment
                  </p>
                </div>
              </form>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default EventDetails;
